import type { ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface TableProps {
  children: ReactNode;
  className?: string;
  /** Caps the height so the header can stick while the body scrolls, e.g. "max-h-96". Omit to let the page scroll. */
  maxHeightClass?: string;
  /** Accessible name for the table. */
  label?: string;
}

/** Contained horizontal scroller, so a wide table never scrolls the whole page on a phone. */
export const Table = ({ children, className, maxHeightClass, label }: TableProps) => (
  <div className={cn('w-full overflow-x-auto', maxHeightClass && 'overflow-y-auto', maxHeightClass)}>
    <table aria-label={label} className={cn('w-full border-collapse text-body', className)}>{children}</table>
  </div>
);

export const THead = ({ children, className }: { children: ReactNode; className?: string }) => (
  <thead className={cn('sticky top-0 z-10 bg-page border-b border-line-strong', className)}>{children}</thead>
);

export const TBody = ({ children, className }: { children: ReactNode; className?: string }) => (
  <tbody className={className}>{children}</tbody>
);

export const TFoot = ({ children, className }: { children: ReactNode; className?: string }) => (
  <tfoot className={className}>{children}</tfoot>
);

interface TrProps {
  children: ReactNode;
  className?: string;
  /** Bold total row with a top border. */
  total?: boolean;
}

export const Tr = ({ children, className, total }: TrProps) => (
  <tr
    className={cn(
      total
        ? 'border-t-2 border-line-strong bg-page font-semibold text-ink'
        : 'border-b border-line hover:bg-page transition-colors',
      className
    )}
  >
    {children}
  </tr>
);

interface CellProps {
  children?: ReactNode;
  className?: string;
  /** Right-align (money and other numbers). */
  numeric?: boolean;
  colSpan?: number;
  rowSpan?: number;
  scope?: 'col' | 'row' | 'colgroup';
}

export const Th = ({ children, className, numeric, colSpan, rowSpan, scope = 'col' }: CellProps) => (
  <th
    scope={scope}
    colSpan={colSpan}
    rowSpan={rowSpan}
    className={cn(
      'px-4 py-3 text-label font-semibold text-neutral whitespace-nowrap',
      numeric ? 'text-right' : 'text-left',
      className
    )}
  >
    {children}
  </th>
);

export const Td = ({ children, className, numeric, colSpan }: CellProps) => (
  <td className={cn('px-4 py-3 text-body text-ink', numeric && 'text-right tabular-nums', className)} colSpan={colSpan}>
    {children}
  </td>
);
